import { supabase } from '../supabaseClient';

// Returns the raw name, or null if there isn't one -- deliberately NOT resolving the
// "A worker" fallback here. This module's output is cached in React state by ActivityFeed
// and re-rendered whenever the language toggles, but a string baked in at fetch time
// wouldn't retranslate on toggle the way a live t() call in JSX does -- see ActivityFeed.jsx,
// which resolves both this fallback and every event's title/subtitle at render time instead.
const workerName = (w) => {
  const name = `${w?.first_name || ''} ${w?.last_name || ''}`.trim();
  return name || null;
};

// One shared activity-event fetcher for both Employer and Union dashboards -- the two sides
// are schema-parallel (jobs/applications vs. apprenticeships/apprenticeship_applications, both
// with an owner column and both referencing the same shared `interviews`/`conversations`/
// `messages` tables), so a single function parameterized by accountType covers both instead of
// duplicating this fan-out per dashboard. Only pulls event types with a real, confirmed column
// to source from: new applications (created_at), new postings (created_at), interviews
// scheduled (created_at -- distinct from interview_at, the future meeting time), and new
// messages actually received from a worker (sender_type='worker', never the account's own
// outgoing messages).
//
// Interviews exclude cancelled/completed rows, matching the "Upcoming interviews" stat card's
// filter on both dashboards (EmployerDashboard.jsx/UnionDashboard.jsx) -- otherwise the feed
// could show an interview the stat card doesn't count, and the two numbers would disagree.
export async function fetchActivityEvents({ accountType, accountId, limit = 15 }) {
  if (!accountId) return [];
  const isUnion         = accountType === 'union';
  const ownerCol         = isUnion ? 'union_id' : 'employer_id';
  const applicationsTbl  = isUnion ? 'apprenticeship_applications' : 'applications';
  const postingsTbl      = isUnion ? 'apprenticeships' : 'jobs';
  const postingIdCol     = isUnion ? 'apprenticeship_id' : 'job_id';
  const postingTitleCol  = isUnion ? 'title' : 'job_title';

  const [{ data: apps }, { data: postings }, { data: interviews }, { data: convs }] = await Promise.all([
    supabase.from(applicationsTbl).select(`id, worker_id, ${postingIdCol}, created_at`).eq(ownerCol, accountId).order('created_at', { ascending: false }).limit(limit),
    supabase.from(postingsTbl).select(`id, ${postingTitleCol}, created_at`).eq(ownerCol, accountId).order('created_at', { ascending: false }).limit(limit),
    supabase.from('interviews').select('id, worker_id, interview_at, created_at').eq(ownerCol, accountId).not('status', 'in', '(cancelled,completed)').order('created_at', { ascending: false }).limit(limit),
    supabase.from('conversations').select('id').eq(ownerCol, accountId),
  ]);

  const convIds = (convs || []).map(c => c.id);
  const { data: messages } = convIds.length > 0
    ? await supabase
        .from('messages')
        .select('id, sender_id, created_at')
        .in('conversation_id', convIds)
        .eq('sender_type', 'worker')
        .order('created_at', { ascending: false })
        .limit(limit)
    : { data: [] };

  // Resolve every referenced worker name and posting title in one batched query each
  // (this feed loads on every dashboard visit, so a fixed number of queries matters more
  // here than in the one-off per-row fan-outs already used elsewhere in these dashboards).
  const workerIds = new Set();
  (apps || []).forEach(a => workerIds.add(a.worker_id));
  (interviews || []).forEach(i => workerIds.add(i.worker_id));
  (messages || []).forEach(m => workerIds.add(m.sender_id));

  const postingIds = new Set((apps || []).map(a => a[postingIdCol]).filter(Boolean));

  const [{ data: workerRows }, { data: postingRows }] = await Promise.all([
    workerIds.size > 0
      ? supabase.from('workers').select('id, first_name, last_name').in('id', [...workerIds])
      : Promise.resolve({ data: [] }),
    postingIds.size > 0
      ? supabase.from(postingsTbl).select(`id, ${postingTitleCol}`).in('id', [...postingIds])
      : Promise.resolve({ data: [] }),
  ]);
  const workerMap  = new Map((workerRows || []).map(w => [w.id, w]));
  const postingMap = new Map((postingRows || []).map(p => [p.id, p[postingTitleCol]]));

  // Every field here is language-independent raw data (names, titles, ISO timestamps) --
  // ActivityFeed.jsx resolves the actual translated title/subtitle strings at render time,
  // so the feed retranslates live on a language toggle instead of staying stuck in whatever
  // language was active when it was fetched.
  const events = [
    ...(apps || []).map(a => ({
      id: `application-${a.id}`, type: 'application', timestamp: a.created_at,
      workerName: workerName(workerMap.get(a.worker_id)),
      postingTitle: postingMap.get(a[postingIdCol]) || null,
    })),
    ...(postings || []).map(p => ({
      id: `posting-${p.id}`, type: 'posting', timestamp: p.created_at,
      postingTitle: p[postingTitleCol] || null,
      isUnion,
    })),
    ...(interviews || []).map(i => ({
      id: `interview-${i.id}`, type: 'interview', timestamp: i.created_at,
      workerName: workerName(workerMap.get(i.worker_id)),
      interviewAt: i.interview_at || null,
    })),
    ...(messages || []).map(m => ({
      id: `message-${m.id}`, type: 'message', timestamp: m.created_at,
      workerName: workerName(workerMap.get(m.sender_id)),
    })),
  ];

  events.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  return events.slice(0, limit);
}

// Trend helper shared by both dashboards' stat cards -- counts how many rows in a list have
// a created_at (or dateKey) within the last 7 days. Always returns a number, including 0 --
// callers render no trend line for a 0 result themselves, rather than a misleading "+0 this week".
export function countThisWeek(rows, dateKey = 'created_at') {
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  return (rows || []).filter(r => r[dateKey] && new Date(r[dateKey]).getTime() >= weekAgo).length;
}
